import { create } from "zustand"

import {
  type CompetitorProduct,
  type CompetitorProductSortField,
  CompetitorProductsListDocument,
  type CompetitorProductsListQuery,
  type SortingOrder,
} from "@/graphql/codegen/graphql"
import { type SearchFilters, createSearchStore } from "@/stores/createSearchStore"

export type CompetitorProductSearchFilters = SearchFilters & {
  field?: CompetitorProductSortField
  order?: SortingOrder
  query?: string
  distributorIds?: string[]
}

const deduplicateCompetitorProducts = (
  newData: CompetitorProduct[],
  existingData: CompetitorProduct[]
): CompetitorProduct[] => {
  const uniqueIds = new Set(existingData.map((product) => product.competitorProductId))
  return [...existingData, ...newData.filter((product) => !uniqueIds.has(product.competitorProductId))]
}

export const useCompetitorProductSearchStore = create(
  createSearchStore<CompetitorProduct, CompetitorProductSearchFilters, CompetitorProductsListQuery>({
    documentNode: CompetitorProductsListDocument,
    deduplicateData: deduplicateCompetitorProducts,
    getQueryInput: (filters: CompetitorProductSearchFilters, offset: number, limit: number) => {
      const { field, order, ...otherFilters } = filters
      return {
        filter: otherFilters,
        offset,
        limit,
        sort: { field, order },
      }
    },
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => ({
      __typename: data.competitorProduct.list.__typename,
      data:
        data.competitorProduct.list.__typename === "CompetitorProductListSuccess"
          ? (data.competitorProduct.list.competitorProducts as CompetitorProduct[])
          : [],
      error:
        data.competitorProduct.list.__typename === "CompetitorProductListFailure"
          ? data.competitorProduct.list.error
          : undefined,
    }),
  })
)
